// Eskom PARCC — single source of truth for the in-app route table.
//
// Both the navigation (Nav) and the router (App) derive from APP_ROUTES, so the
// set of screens — and crucially which ones are admin-gated — is declared once.
// Previously the admin set was hand-listed twice (Nav's `roles` flags and App's
// `adminOnly(...)` wraps); the two could drift, and forgetting an App-side wrap
// is a silent security gap (a deep link would render the gated screen). Adding a
// page is now a one-line edit here, and gating cannot diverge between the two.
//
// This is a `.tsx` file because each route carries a JSX `element`.
import type { ReactElement } from 'react';
import {
  LayoutDashboard,
  FolderOpen,
  ClipboardList,
  Search,
  Compass,
  Wrench,
  Upload,
  FileSpreadsheet,
  FileText,
  BookOpen,
  Users,
  DatabaseBackup,
  Bell,
  Sparkles,
  ShieldCheck,
  History,
  type LucideIcon,
} from 'lucide-react';
import type { RouteKey } from '../../lib/permissions';
import { DashboardPage } from '../dashboard/DashboardPage';
import { TransactionsPage } from '../transactions/TransactionsPage';
import { WorkingPapersPage } from '../working-papers/WorkingPapersPage';
import { FindingsPage } from '../findings/FindingsPage';
import { AllocationPage } from '../allocation/AllocationPage';
import { ManagePage } from '../manage/ManagePage';
import { ImportPage } from '../import/ImportPage';
import { BulkUpdatePage } from '../bulk-update/BulkUpdatePage';
import { DataSafetyPage } from '../safety/DataSafetyPage';
import { ReportsPage } from '../reports/ReportsPage';
import { AiAssurancePage } from '../reports/AiAssurancePage';
import { AuditTrailPage } from '../audit/AuditTrailPage';
import { RulesPage } from '../rules/RulesPage';
import { UsersPage } from '../users/UsersPage';
import { NotificationsAdminPage } from '../notifications/NotificationsAdminPage';
import { CesPage } from '../ces/CesPage';
import { CesUploadPage } from '../ces/CesUploadPage';
import { SettingsPage } from '../settings/SettingsPage';
import { RtmComposerPage } from '../reports/rtm/RtmComposerPage';
import { AiWorkspacePage } from '../ai/AiWorkspacePage';

/** Sidebar sections in the Lumen shell. Legacy's flat tab strip ignores these. */
export type NavGroup = 'overview' | 'review' | 'data' | 'outputs' | 'admin';

/** Group headings, in render order. */
export const NAV_GROUPS: readonly { key: NavGroup; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'review', label: 'Review workflow' },
  { key: 'data', label: 'Data' },
  { key: 'outputs', label: 'Outputs & reference' },
  { key: 'admin', label: 'Administration' },
];

export interface AppRoute {
  /** Router path segment, relative to the layout route. Also the capability key
   *  passed to {@link canAccessRoute}. E.g. `'dashboard'`. */
  path: RouteKey;
  /** Nav label (text). E.g. `'Dashboard'`. */
  label: string;
  /** Nav icon (lucide). */
  icon: LucideIcon;
  /** The screen rendered at this path. */
  element: ReactElement;
  /** Sidebar section (Lumen). Legacy renders one flat strip and ignores it. */
  group: NavGroup;
  /** Page heading in the Lumen header. */
  title: string;
  /** One-line description under the heading (Lumen header). */
  desc: string;
  /**
   * Lumen folds this screen into another sidebar item (it renders a segmented
   * control to switch between them), while Legacy keeps both as separate tabs.
   * Bulk Update sits under Import & Bulk. A deep link to the child still
   * highlights the parent.
   */
  lumenParent?: RouteKey;
  /** Lumen label override — used where the folded item needs a broader name. */
  lumenLabel?: string;
  /**
   * Small sidebar decoration (Lumen only), e.g. `'NEW'`. Purely cosmetic — it is
   * rendered `aria-hidden`, so it never becomes part of the link's accessible
   * name, and it is NOT a capability: visibility is still `canAccessRoute`.
   */
  badge?: string;
  /**
   * Render this screen with NO page padding, in BOTH shells.
   *
   * The AI workspace is a full-bleed chat surface, not a card layout. The flag
   * lives on the route table rather than as a `pathname === '/aiworkspace'`
   * check inside each shell precisely because the duplicated version is how this
   * came to be missing from both shells in the first place — one source of
   * truth, and a test can assert the whole set (see shellMainPadding.test.tsx).
   */
  fullBleed?: boolean;
  /**
   * Widen `<main>` for this route: drop the centred max-width cap but KEEP the
   * page padding, so a work-dense screen uses the whole monitor. Distinct from
   * `fullBleed`, which also strips padding for edge-to-edge chat surfaces. Lumen's
   * `<main>` is already uncapped, so this is mainly the Legacy half.
   */
  wide?: boolean;
}

// Order mirrors the monolith's nav. Visibility + deep-link access are both decided
// by `canAccessRoute(path, grant)` (src/lib/permissions) — keyed off the path — so
// the expanded role set (admin/manager/teamLead/reviewer/dataCoordinator) is gated
// in ONE place. Icons replace the old emoji.
export const APP_ROUTES: readonly AppRoute[] = [
  {
    path: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    element: <DashboardPage />,
    group: 'overview',
    title: 'Compliance recovery cockpit',
    desc: 'Review progress, irregular-expenditure exposure and regulatory breaches across Eskom procurement routes.',
  },
  {
    // Directly below Dashboard, per the design handoff. Gated on
    // AccessGrant.aiEnabled (canAccessRoute → canUseAi), so it is absent for
    // everybody until an admin grants it — including for existing roles, which
    // is why the nav/parity fixtures stayed green when this landed.
    path: 'aiworkspace',
    label: 'AI Workspace',
    icon: Sparkles,
    element: <AiWorkspacePage />,
    group: 'overview',
    title: 'AI compliance workspace',
    desc: 'Ask the compliance engine about the population, the rubric or a finding — grounded in what you can already see.',
    badge: 'NEW',
    fullBleed: true,
  },
  {
    path: 'transactions',
    label: 'Transactions',
    icon: FolderOpen,
    element: <TransactionsPage />,
    group: 'review',
    title: 'Transactions register',
    desc: 'The full procurement population — filter, review and drill into any transaction.',
  },
  {
    path: 'working-papers',
    label: 'Working Papers',
    icon: ClipboardList,
    element: <WorkingPapersPage />,
    group: 'review',
    title: 'Working Papers',
    desc: 'Allocated transactions and their Compliance Evaluation Sheets.',
  },
  {
    path: 'findings',
    label: 'Findings',
    icon: Search,
    element: <FindingsPage />,
    group: 'review',
    title: 'Findings register',
    desc: 'Every distinct non-compliant finding, joined to its transaction.',
  },
  {
    path: 'allocation',
    label: 'Allocation',
    icon: Compass,
    element: <AllocationPage />,
    group: 'review',
    title: 'Allocation',
    desc: 'Assign and reassign transactions to reviewers in bulk.',
  },
  {
    path: 'manage',
    label: 'Manage',
    icon: Wrench,
    element: <ManagePage />,
    group: 'data',
    title: 'Manage Transactions',
    desc: 'Edit base fields, add or remove transactions.',
  },
  {
    path: 'import',
    label: 'Import',
    lumenLabel: 'Import & Bulk',
    icon: Upload,
    element: <ImportPage />,
    group: 'data',
    title: 'Import & Bulk Update',
    desc: 'Load, replace or bulk-edit the transaction population from Excel.',
  },
  // R6: registered HERE and not in EXTRA_ROUTES — App wraps APP_ROUTES in
  // <RequireRoute>, so a deep link to a screen that writes the full manual-editor
  // surface is gated by the router, not only by the in-page check.
  {
    path: 'bulk-update',
    label: 'Bulk Update',
    icon: FileSpreadsheet,
    element: <BulkUpdatePage />,
    group: 'data',
    title: 'Import & Bulk Update',
    desc: 'Update transactions that already exist in the register, cell by cell.',
    // Lumen shows ONE "Import & Bulk" sidebar item with a segmented control;
    // Legacy keeps the two tabs it has always had.
    lumenParent: 'import',
  },
  {
    path: 'data-safety',
    label: 'Data Safety',
    icon: DatabaseBackup,
    element: <DataSafetyPage />,
    group: 'admin',
    title: 'Data Safety',
    desc: 'Rolling snapshots and point-in-time restore.',
  },
  {
    path: 'reports',
    label: 'Reports',
    icon: FileText,
    element: <ReportsPage />,
    group: 'outputs',
    title: 'Reports',
    desc: 'Generate the multi-slide audit recovery report and division summaries.',
  },
  {
    // Gated on canUseAi + report-lead role (see canAccessRoute). Absent for
    // everyone until an admin grants AI, exactly like the AI Workspace.
    path: 'ai-assurance',
    label: 'AI Assurance',
    icon: ShieldCheck,
    element: <AiAssurancePage />,
    group: 'outputs',
    title: 'AI Assurance',
    desc: 'Coverage and AI-vs-reviewer agreement across every AI-reviewed transaction.',
    badge: 'NEW',
  },
  {
    path: 'audit-trail',
    label: 'Audit Trail',
    icon: History,
    element: <AuditTrailPage />,
    group: 'outputs',
    title: 'Audit Trail',
    desc: 'Every recorded change — who, what, where and when — across the transactions you can see.',
    // Dense six-column table (When · By · Action · Surface · Reference · What changed);
    // drop the centred max-width cap so the columns are not squeezed.
    wide: true,
  },
  {
    path: 'rules',
    label: 'Rule Book',
    icon: BookOpen,
    element: <RulesPage />,
    group: 'outputs',
    title: 'Rule Book',
    desc: 'How findings are classified and how CES status maps to a register level.',
  },
  {
    path: 'users',
    label: 'Users',
    lumenLabel: 'Users & Access',
    icon: Users,
    element: <UsersPage />,
    group: 'admin',
    title: 'Users & Access',
    desc: 'Grant, edit and revoke access; review the audit trail.',
  },
  {
    path: 'notifications',
    label: 'Notifications',
    icon: Bell,
    element: <NotificationsAdminPage />,
    group: 'admin',
    title: 'Notifications',
    desc: 'Control per-person delivery of in-app and email alerts.',
  },
];

/**
 * Routes that exist in the router but NOT in the navigation (deep-linked
 * screens). Permission is enforced in-page: the CES capture page guards via
 * canEditTransaction (admin, or the transaction's assignee), so it carries no
 * blanket `role` flag here.
 */
export const EXTRA_ROUTES: readonly {
  path: string;
  element: ReactElement;
  title?: string;
  desc?: string;
  wide?: boolean;
}[] = [
  // 9 Jun call: the CES capture is a full page (was a cramped modal).
  {
    path: 'ces/:ref',
    element: <CesPage />,
    title: 'Compliance Evaluation Sheet',
    desc: 'Capture the working-paper criteria, findings and compliance for a transaction.',
    // The capture page carries the AI review, the criteria checklist and the
    // evidence — give it the full monitor width (padding kept), not the centred
    // ~1680px column that leaves the analysis cramped on a big screen.
    wide: true,
  },
  {
    path: 'ces-upload',
    element: <CesUploadPage />,
    title: 'CES workbook upload',
    desc: 'Import completed Compliance Evaluation Sheets from an Excel workbook.',
  },
  // Self-service workspace settings — appearance (Legacy/Lumen), notifications,
  // regional format and session security. Every authenticated user may reach it
  // (there is nothing here but their own preferences), so it carries no
  // capability key and is not in the navigation; both shells link to it.
  {
    path: 'settings',
    element: <SettingsPage />,
    title: 'Settings',
    desc: 'Appearance, theme, notifications and workspace preferences.',
  },
  // Phase 2 (11 Sep 2026): the Report-to-Management composer — a deep link per
  // FY × quarter × division from the Reports page. Its own first segment ('rtm')
  // so isWideRoute widens it (the document tables + finding editors need the width);
  // authoring is gated in-page by canAuthorRtm and server-side by admin-api.
  {
    path: 'rtm/:fy/:quarter/:division',
    element: <RtmComposerPage />,
    title: 'Report to Management',
    desc: 'Draft, edit and issue a division’s Report to Management for the quarter.',
    wide: true,
  },
];

/** Page heading + description for a pathname, for the Lumen header. */
export function routeMeta(pathname: string): { title: string; desc: string } {
  const segment = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  const app = APP_ROUTES.find((r) => r.path === segment);
  if (app) return { title: app.title, desc: app.desc };
  const extra = EXTRA_ROUTES.find((r) => r.path.split('/')[0] === segment);
  if (extra?.title) return { title: extra.title, desc: extra.desc ?? '' };
  return { title: 'Eskom PARCC', desc: 'Procurement Audit Recovery Command Centre.' };
}

/**
 * Should `<main>` drop its page padding for this pathname?
 *
 * Deliberately uses the SAME segment extraction as {@link routeMeta}, so a deep
 * link (`/aiworkspace/anything`) behaves identically in both. Both shells call
 * this — Legacy falling behind Lumen is exactly the failure mode this replaces.
 */
export function isFullBleedRoute(pathname: string): boolean {
  const segment = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  return APP_ROUTES.some((r) => r.path === segment && r.fullBleed === true);
}

/**
 * Should `<main>` use the full monitor width for this pathname — uncapped, but
 * still padded? Same first-segment match as {@link isFullBleedRoute}, and scans
 * BOTH tables because the CES capture page lives in EXTRA_ROUTES, not APP_ROUTES.
 */
export function isWideRoute(pathname: string): boolean {
  const segment = pathname.replace(/^\/+/, '').split('/')[0] ?? '';
  return (
    APP_ROUTES.some((r) => r.path === segment && r.wide === true) ||
    EXTRA_ROUTES.some((r) => r.path.split('/')[0] === segment && r.wide === true)
  );
}

/**
 * The `<main>` className for a pathname: composes the route-level page treatments
 * (`is-bleed`, `is-wide`) into one string, or `undefined` for the default padded,
 * centred page. Both shells call this so neither can silently drift from the other.
 */
export function mainClass(pathname: string): string | undefined {
  const classes = [
    isFullBleedRoute(pathname) ? 'is-bleed' : '',
    isWideRoute(pathname) ? 'is-wide' : '',
  ].filter(Boolean);
  return classes.length > 0 ? classes.join(' ') : undefined;
}
